import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api.js';
import { t, getal, wanneer, geleden } from '../i18n.js';
import { St } from './Status.jsx';
import Wachtwoordveld from './Wachtwoordveld.jsx';

/**
 * De koppeling met Cisco Duo (docs/ontwerp/duo.md): de sleutel van het MSP-account (Accounts API; getest en versleuteld
 * bewaard), en per subaccount welke klant in ZERO het is. Alleen een gekoppeld subaccount wordt opgehaald.
 */
export default function DuoKoppeling({ klanten }) {
  const client = useQueryClient();
  const q = useQuery({ queryKey: ['duo-koppeling'], queryFn: api.duoKoppeling, refetchInterval: 15_000 });
  const [wijzig, zetWijzig] = useState(false);
  const [melding, zetMelding] = useState(null);
  if (q.isPending) return <p className="muted">{t('Laden…')}</p>;
  if (q.isError) return <div className="melder rood">{t(q.error.message)}</div>;
  const k = q.data.data;
  const doe = async (fn, goed) => {
    zetMelding(null);
    try { await fn(); if (goed) zetMelding({ goed: true, tekst: goed }); } catch (e) { zetMelding({ goed: false, tekst: t(e.details ? Object.values(e.details)[0] : e.message) }); }
    client.invalidateQueries({ queryKey: ['duo-koppeling'] });
  };
  const bezet = new Set(k.accounts.filter((a) => a.tenant).map((a) => a.tenant.code));

  return (
    <div className="grid-2">
      <div className="stack">
        <section className="card">
          <header><h2>Cisco Duo</h2>{k.linked && <span className="meta">{k.syncError ? <St soort="critical">{t('Fout bij ophalen')}</St> : <St soort="good">{t('Gekoppeld')}</St>}</span>}</header>
          {melding && <div className={`melder ${melding.goed ? 'blauw' : 'rood'}`} style={{ marginBottom: 10 }}>{melding.tekst}</div>}
          {k.linked && !wijzig ? (
            <>
              <dl className="facts">
                <dt>{t('API-hostname')}</dt><dd className="mono">{k.host ?? '—'}</dd>
                <dt>{t('Ingesteld')}</dt><dd>{wanneer(k.setAt)}{k.setBy ? ` · ${k.setBy}` : ''}</dd>
                <dt>{t('Laatst opgehaald')}</dt><dd>{k.syncedAt ? geleden(k.syncedAt) : t('nog niet')}</dd>
                <dt>{t('Subaccounts')}</dt><dd>{getal(k.accounts.length)} · {t('{n} gekoppeld', { n: k.accounts.filter((a) => a.tenant).length })}</dd>
              </dl>
              {k.syncError && <div className="callout warn" style={{ marginTop: 10 }}>{k.syncError}</div>}
              <div className="actions">
                <button type="button" className="btn primary" onClick={() => doe(api.duoNu, t('Ophalen gestart; de uitkomst staat zo hieronder.'))}>{t('Nu ophalen')}</button>
                <button type="button" className="btn" onClick={() => doe(api.duoAccountsVernieuwen, t('De subaccounts zijn bijgewerkt.'))}>{t('Subaccounts vernieuwen')}</button>
                <button type="button" className="btn" onClick={() => zetWijzig(true)}>{t('Sleutel wijzigen')}</button>
                <button type="button" className="btn danger" onClick={() => doe(api.verwijderDuoKoppeling)}>{t('Koppeling verwijderen')}</button>
              </div>
              <p className="noot">{t('ADTS ZERO haalt elke vijf minuten de authenticaties en het beheer op, met twee minuten vertraging. Alleen lezen: ADTS ZERO wijzigt niets in Duo.')}</p>
            </>
          ) : <Instellen onKlaar={(n) => { zetWijzig(false); zetMelding({ goed: true, tekst: t('Gekoppeld; {n} subaccounts.', { n }) }); client.invalidateQueries({ queryKey: ['duo-koppeling'] }); }}
            onAnnuleer={k.linked ? () => zetWijzig(false) : null} />}
        </section>
        {k.linked && (() => {
          const hoofdErin = k.accounts.some((a) => a.id === 'hoofdaccount');
          return (
          <section className="card">
            <header><h2>{t('Hoofdaccount')}</h2>{hoofdErin && <span className="meta"><St soort="good">{t('Toegevoegd')}</St></span>}</header>
            <p className="muted">{t('De logins in het hoofdaccount zelf (ADTS) vallen buiten de Accounts API. Vaak kan de huidige sleutel ze wél lezen; probeer dat eerst. Lukt dat niet, gebruik dan een Admin API-applicatie in het hoofdaccount (met logs lezen). Daarna staat het hoofdaccount in de lijst; koppel het aan ADTS.')}</p>
            {hoofdErin ? (
              <div className="actions"><button type="button" className="btn danger" onClick={() => doe(api.verwijderDuoHoofdaccount)}>{t('Hoofdaccount verwijderen')}</button></div>
            ) : (
              <>
                <div className="actions"><button type="button" className="btn primary"
                  onClick={() => doe(api.duoHoofdaccountHuidig, t('Hoofdaccount toegevoegd; koppel het hoofdaccount rechts aan ADTS.'))}>{t('Toevoegen met de huidige sleutel')}</button></div>
                <details style={{ marginTop: 10 }}>
                  <summary className="muted" style={{ cursor: 'pointer' }}>{t('Werkt dat niet? Een aparte Admin API-sleutel gebruiken')}</summary>
                  <div style={{ marginTop: 10 }}>
                    <Hoofdaccount onKlaar={() => { zetMelding({ goed: true, tekst: t('Sleutel voor het hoofdaccount ingesteld; koppel het hoofdaccount rechts aan ADTS.') }); client.invalidateQueries({ queryKey: ['duo-koppeling'] }); }} />
                  </div>
                </details>
              </>
            )}
          </section>
          );
        })()}
      </div>

      <div className="stack">
        <section className="card">
          <header><h2>{t('Subaccounts in Duo')}</h2><span className="meta">{getal(k.accounts.length)}</span></header>
          {k.accounts.length ? (
            <table>
              <thead><tr><th>Duo</th><th>{t('Klant in ADTS ZERO')}</th><th>{t('Laatst')}</th></tr></thead>
              <tbody>{k.accounts.map((a) => (
                <tr key={a.id}>
                  <td>{a.name}<br /><span className="muted mono" style={{ fontSize: 12 }}>{a.host}</span></td>
                  <td><select className="input" value={a.tenant?.code ?? ''} aria-label={t('Klant in ADTS ZERO voor {org}', { org: a.name })}
                    onChange={(e) => doe(() => api.koppelDuoAccount(a.id, e.target.value || null))}>
                    <option value="">{t('— niet koppelen —')}</option>
                    {klanten.filter((x) => x.active && (!bezet.has(x.code) || x.code === a.tenant?.code)).map((x) => <option key={x.code} value={x.code}>{x.name}</option>)}
                  </select></td>
                  <td style={{ fontSize: 13 }}>{!a.tenant ? <span className="muted">—</span>
                    : a.syncError ? <span title={a.syncError}><St soort="critical">{t('fout')}</St> <span className="muted">{a.syncError.slice(0, 80)}</span></span>
                    : a.syncedAt ? <>{geleden(a.syncedAt)}<br /><span className="muted">{t('{n} authenticaties', { n: getal(a.events) })}</span></>
                    : <span className="muted">{t('nog niet')}</span>}</td>
                </tr>
              ))}</tbody>
            </table>
          ) : <p className="empty">{t('Nog geen subaccounts: stel eerst de koppeling in.')}</p>}
          <p className="noot">{t('Koppel elk subaccount aan de klant in ADTS ZERO en zet bij die klant de module MFA (Duo) aan. Een subaccount dat niet gekoppeld is, haalt ADTS ZERO niet op.')}</p>
        </section>
      </div>
    </div>
  );
}

function Instellen({ onKlaar, onAnnuleer }) {
  const [v, zetV] = useState({ ikey: '', skey: '', host: '' });
  const [fout, zetFout] = useState(null);
  const [bezig, zetBezig] = useState(false);
  const zet = (k) => (e) => zetV((x) => ({ ...x, [k]: e.target.value }));
  return (
    <form className="form" onSubmit={async (e) => {
      e.preventDefault(); zetFout(null); zetBezig(true);
      try { onKlaar((await api.zetDuoKoppeling({ ikey: v.ikey.trim(), skey: v.skey.trim(), host: v.host.trim() })).data.accounts); }
      catch (err) { zetFout(t(err.details ? Object.values(err.details)[0] : err.message)); } finally { zetBezig(false); }
    }}>
      {fout && <div className="melder rood">{fout}</div>}
      <label className="field"><span>Integration key</span><input className="input mono" value={v.ikey} onChange={zet('ikey')} autoComplete="off" placeholder="DIXXXXXXXXXXXXXXXXXX" /></label>
      <label className="field"><span>Secret key</span><Wachtwoordveld waarde={v.skey} zet={(w) => zetV((x) => ({ ...x, skey: w }))} autoComplete="new-password" /></label>
      <label className="field"><span>{t('API-hostname')}</span><input className="input mono" value={v.host} onChange={zet('host')} autoComplete="off" placeholder="api-xxxxxxxx.duosecurity.com" /></label>
      <div className="actions">
        <button className="btn primary" disabled={bezig || !v.ikey.trim() || !v.skey.trim() || !v.host.trim()}>{bezig ? t('Bezig…') : t('Testen en opslaan')}</button>
        {onAnnuleer && <button type="button" className="btn" onClick={onAnnuleer}>{t('Annuleren')}</button>}
      </div>
      <p className="noot">{t('Maak in het MSP-account van ADTS onder Applications een Accounts API-applicatie, en geef die toegang tot de Admin API van de subaccounts (alleen lezen: logs). ADTS ZERO test de sleutel eerst en bewaart hem versleuteld; hij komt niet terug in het scherm.')}</p>
    </form>
  );
}

function Hoofdaccount({ onKlaar }) {
  const [v, zetV] = useState({ ikey: '', skey: '' });
  const [fout, zetFout] = useState(null);
  const [bezig, zetBezig] = useState(false);
  return (
    <form className="form" onSubmit={async (e) => {
      e.preventDefault(); zetFout(null); zetBezig(true);
      try { await api.zetDuoHoofdaccount({ ikey: v.ikey.trim(), skey: v.skey.trim() }); onKlaar(); }
      catch (err) { zetFout(t(err.details ? Object.values(err.details)[0] : err.message)); } finally { zetBezig(false); }
    }}>
      {fout && <div className="melder rood">{fout}</div>}
      <label className="field"><span>Integration key (Admin API)</span>
        <input className="input mono" value={v.ikey} onChange={(e) => zetV((x) => ({ ...x, ikey: e.target.value }))} autoComplete="off" placeholder="DIXXXXXXXXXXXXXXXXXX" /></label>
      <label className="field"><span>Secret key</span><Wachtwoordveld waarde={v.skey} zet={(w) => zetV((x) => ({ ...x, skey: w }))} autoComplete="new-password" /></label>
      <div className="actions"><button className="btn primary" disabled={bezig || !v.ikey.trim() || !v.skey.trim()}>{bezig ? t('Bezig…') : t('Testen en opslaan')}</button></div>
    </form>
  );
}
